'use client';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { memo, useEffect } from 'react';

import { useRealtorInfo } from '@/app/(realtor)/realtor/[realtorCd]/api';
import { RealtorInfoDataType } from '@/app/(realtor)/realtor/[realtorCd]/type';
import useMobileStore from '@/store/mobileStore';
import { handleSendGAEvent } from '@/utils/google';
import useControlTitle from '@/utils/hooks/useControlTitle';
import { convertCdnUrl } from '@/utils/js/condition';
import { appToToast } from '@/utils/js/webView';

const InfoMap = dynamic(() => import('@/app/(realtor)/realtor/[realtorCd]/ui/infoMap'), {
  ssr: false,
});

function Info() {
  const { isWebView } = useMobileStore();
  const { realtorCd } = useParams();
  const { data } = useRealtorInfo(realtorCd as string);
  const { ref } = useControlTitle();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleFindPath = (lat: string, lng: string, name: string) => {
    if (typeof window === 'undefined') return;
    if (window.webkit) {
      const clickedAt = +new Date();
      location.href = `nmap://place?lat=${lat}&lng=${lng}&name=${name}&appname=kr.co.proptier.town`;
      setTimeout(function () {
        if (+new Date() - clickedAt < 2000) {
          location.href = 'http://itunes.apple.com/app/id311867728?mt=8';
        }
      }, 1500);
    } else if (window.Android) {
      window.location.href = `intent://place?lat=${lat}&lng=${lng}&name=${name}&appname=kr.co.proptier.town#Intent;scheme=nmap;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;package=com.nhn.android.nmap;end`;
    } else {
      location.href = `http://m.map.naver.com/route.nhn?menu=route&ename=${name}&ex=${lng}&ey=${lat}&pathType=0&showMap=true`;
    }
  };

  const callToNum = (num: any) => {
    location.href = 'tel:' + num;
  };

  const handleBannerLogic = (data: RealtorInfoDataType | undefined) => {
    if (!data) return undefined;
    const { sSafeRealestateAgencyYN } = data;
    if (sSafeRealestateAgencyYN === 'Y') return 'care';
    return undefined;
  };

  useEffect(() => {
    if (realtorCd) {
      if (isWebView) {
        handleSendGAEvent('page_view', {
          event_category: 'realtor',
          page_title: 'realtor_detail',
          realtor_id: realtorCd,
        });
      } else {
        handleSendGAEvent('page_view', {
          event_category: 'share',
          page_title: 'share_realtor',
          realtor_id: realtorCd,
        });
      }
    }
  }, [realtorCd]);

  return (
    <div className="relator_information_wrap">
      <div className="wrap_band_banner">
        {handleBannerLogic(data) === 'care' && (
          <Link
            href={process.env.CC_WEB_URL ? `${process.env.CC_WEB_URL}?kind=town` : ''}
            className="banner safety_care"
            onClick={(e) => {
              // if (!isWebView) e.preventDefault();
              handleSendGAEvent('realtor_detail_banner_care_btn', {
                event_category: 'realtor',
              });
            }}
          >
            <Image
              src={convertCdnUrl('/propfintech/care_banner_realtor_detail_text.svg')}
              alt="안심하며 계약하고 안전하게 사는, 안심케어 프리미엄"
              width={361}
              height={49}
            />
          </Link>
        )}
      </div>
      <div ref={ref}></div>
      <div className="relator_information">
        <div className="relator_name">
          <div className="tag_div">
            {data?.sSafeRealestateAgencyYN === 'Y' ? (
              <span className="tag_care"></span>
            ) : data?.sSafeCareJoinYN === 'Y' ? (
              <span className="tag_care_join"></span>
            ) : (
              ''
            )}
          </div>
          <p className="name">{data?.rname}</p>
        </div>
        <div className="information">
          <div className="call divide_box">
            {data?.cphone && (
              <button onClick={() => callToNum(data?.cphone)}>
                <i className="icon_phone_fill_gray"></i>
                {data?.cphone}
              </button>
            )}
            {data?.rphone && (
              <button onClick={() => callToNum(data?.rphone)}>
                <i className="icon_call_fill_gray"></i>
                {data?.rphone}
              </button>
            )}
          </div>
          <div className="detail divide_box">
            <p>
              <b>대표</b> {data?.name}
            </p>
            <p>
              <b>등록번호</b> {data?.estbRegstNo}
            </p>
          </div>
        </div>
        {data?.xpos && data?.ypos && (
          <div className="map_wrap">
            <InfoMap coordinate={{ xPos: data?.xpos, yPos: data?.ypos }} />
          </div>
        )}
        {data?.addr && (
          <div className="address">
            <p>{data?.addr}</p>
            <button
              onClick={() => {
                if (window.webkit) {
                  appToToast({ message: '클립보드에 복사되었어요', imageName: 'icon_success' });
                }
                navigator.clipboard.writeText(data?.addr);
              }}
            >
              복사
            </button>
          </div>
        )}
        {data?.ypos && data?.xpos && (
          <button
            onClick={() => {
              handleSendGAEvent('realtor_detail_findway_btn', {
                event_category: 'realtor',
              });
              handleFindPath(data?.ypos, data?.xpos, data?.rname);
            }}
            className="btn_find_road"
          >
            길찾기
            <i className="icon_arrow"></i>
          </button>
        )}
      </div>
    </div>
  );
}

export default memo(Info);
